/**
 * CustomerLicenses — License and subscription management
 *
 * IT admins can see their current plan, active user count,
 * graduated volume pricing breakdown, and usage stats.
 */

import { useState, useEffect } from 'react';
import {
  makeStyles,
  Text,
  Card,
  Badge,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import {
  Money24Regular,
  PeopleTeam24Regular,
  Chat24Regular,
  Receipt24Regular,
  Tag24Regular,
} from '@fluentui/react-icons';
import { useApiClient, customerService, type TenantSubscriptionInfo } from '../../services/apiService';
import {
  calculatePricingWithTiers,
  formatCurrency,
  formatRate,
} from '../../../../shared/src/pricing';
import { SPACING, TYPOGRAPHY } from '../../styles/constants';

const useStyles = makeStyles({
  page: { maxWidth: '800px', padding: SPACING.lg },
  pageHeader: { display: 'flex', flexDirection: 'column', marginBottom: SPACING.xl },
  title: { ...TYPOGRAPHY.h2, marginBottom: SPACING.xs },
  subtitle: { ...TYPOGRAPHY.bodySmall, color: tokens.colorNeutralForeground3, marginBottom: 0 },
  planCard: {
    padding: SPACING.xl,
    marginBottom: SPACING.xl,
    border: `2px solid ${tokens.colorBrandBackground}`,
  },
  planHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.lg,
  },
  planName: { fontSize: '24px', fontWeight: 700 },
  planStatus: { display: 'flex', alignItems: 'center', gap: SPACING.md },
  planDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '20px',
  },
  planDetail: { textAlign: 'center' },
  detailValue: { fontSize: '28px', fontWeight: '700', color: tokens.colorBrandForeground1 },
  detailLabel: { fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '4px' },
  usageCard: { padding: '24px', marginBottom: '20px' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '16px' },
  usageRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  usageLabel: { fontSize: '14px' },
  usageValues: { display: 'flex', alignItems: 'center', gap: '12px' },
  usageBar: {
    width: '120px',
    height: '6px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '3px',
    overflow: 'hidden',
  },
  usageFill: { height: '100%', borderRadius: '3px' },
  usageText: { fontSize: '13px', color: tokens.colorNeutralForeground3, minWidth: '80px', textAlign: 'right' },
  pricingCard: { padding: '24px', marginBottom: '20px' },
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '12px',
    marginTop: '12px',
    marginBottom: '16px',
  },
  pricingBand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '12px 8px',
    borderRadius: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  pricingBandActive: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '12px 8px',
    borderRadius: '8px',
    backgroundColor: tokens.colorBrandBackground2,
    border: `2px solid ${tokens.colorBrandBackground}`,
  },
  pricingRate: { fontSize: '20px', fontWeight: '700', color: tokens.colorBrandForeground1, textAlign: 'center' as const },
  pricingRange: { fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '4px', textAlign: 'center' as const },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  breakdownTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0 0',
    borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
    marginTop: '8px',
    fontWeight: '700',
    fontSize: '15px',
  },
  contactCard: { padding: '24px', textAlign: 'center' },
  contactText: { fontSize: '14px', color: tokens.colorNeutralForeground3, marginBottom: '16px' },
  activeNote: { fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '12px', fontStyle: 'italic' },
  invoicesTable: { width: '100%', borderCollapse: 'collapse' as const },
  invoiceRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '12px 0',
  },
  invoiceHeaderRow: {
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    padding: '12px 0',
    fontWeight: 600,
    fontSize: '13px',
  },
  invoiceCell: { padding: '12px 8px 12px 0', fontSize: '13px' },
  invoiceAmount: { textAlign: 'right' as const, fontWeight: 600, color: tokens.colorBrandForeground1 },
  estimateCard: {
    padding: '24px',
    marginBottom: '20px',
    border: `2px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  estimateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  estimateAmount: {
    fontSize: '32px',
    fontWeight: '700',
    color: tokens.colorBrandForeground1,
  },
  estimateLabel: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  offerCard: { padding: '24px', marginBottom: '20px' },
  offerList: { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' },
  offerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  offerName: { fontWeight: 600 },
  offerMeta: { fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '4px' },
  offerDetails: { display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' },
  savingsText: { color: tokens.colorPaletteGreenForeground1, fontWeight: 600 },
  sparklineContainer: { marginTop: SPACING.lg, paddingTop: SPACING.lg, borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  sparklineLabel: { fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: SPACING.md },
  sparklineSvg: { width: '100%', height: '60px' },
});

export default function CustomerLicenses() {
  const styles = useStyles();
  useApiClient();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<TenantSubscriptionInfo | null>(null);

  useEffect(() => {
    customerService.getSubscription()
      .then((s) => setSubscription(s))
      .catch((err) => console.error('Failed to load subscription:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '48px', textAlign: 'center' }}><Spinner /></div>;
  if (!subscription) return <div style={{ padding: '48px', textAlign: 'center' }}><Text>Failed to load subscription data.</Text></div>;

  const sub = subscription;
  const basePricingTiers = Array.isArray(sub.pricingTiers) ? sub.pricingTiers : [];
  const effectivePricingTiers = Array.isArray(sub.effectivePricingTiers) && sub.effectivePricingTiers.length > 0
    ? sub.effectivePricingTiers
    : basePricingTiers;
  const pricing = calculatePricingWithTiers(sub.activeUsers ?? 0, effectivePricingTiers, sub.invoiceDiscounts);
  const basePricing = calculatePricingWithTiers(sub.activeUsers ?? 0, basePricingTiers);
  const finalEffectiveRate = pricing.users > 0 ? pricing.finalCost / pricing.users : 0;

  const getBaseRate = (segmentMin: number, segmentMax: number | null) => {
    const baseTier = basePricingTiers.find((tier) => (
      tier.min <= segmentMin && (
        (tier.max === null && segmentMax === null) ||
        (tier.max !== null && segmentMax !== null && tier.max >= segmentMax) ||
        (tier.max === null && segmentMax !== null)
      )
    ));
    return baseTier?.rate ?? null;
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <Text className={styles.title}>Licensing & Billing</Text>
        <Text className={styles.subtitle}>Pricing, usage, and invoices for your Sherpai subscription</Text>
      </div>

      {sub.appliedOffers && sub.appliedOffers.length > 0 && (
        <Card className={styles.offerCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag24Regular style={{ color: tokens.colorBrandForeground1 }} />
            <Text className={styles.sectionTitle} style={{ marginBottom: 0 }}>Active pricing offers</Text>
          </div>
          <div className={styles.offerList}>
            {sub.appliedOffers.map((offer) => (
              <div key={`${offer.source}-${offer.id ?? offer.name}`} className={styles.offerRow}>
                <div>
                  <Text className={styles.offerName}>{offer.name}</Text>
                  <Text className={styles.offerMeta}>
                    {offer.source === 'group' ? 'Customer group pricing' : offer.source === 'promotion' ? 'Promotional pricing' : 'Discount code'}
                    {offer.startsAt || offer.endsAt
                      ? ` • ${offer.startsAt ? new Date(offer.startsAt).toLocaleDateString() : 'Now'} to ${offer.endsAt ? new Date(offer.endsAt).toLocaleDateString() : 'Until removed'}`
                      : ''}
                  </Text>
                </div>
                <div className={styles.offerDetails}>
                  {offer.bandDiscounts?.map((band) => (
                    <Badge key={`${band.min}-${band.max ?? 'plus'}-${band.percentOff}`} appearance="tint" color="informative">
                      {band.min}-{band.max ?? '+'} users: {band.percentOff}% off RRP
                    </Badge>
                  ))}
                  {offer.invoiceDiscount && (
                    <Badge appearance="filled" color="success">
                      {offer.invoiceDiscount.type === 'percentage'
                        ? `${offer.invoiceDiscount.value}% invoice discount`
                        : `${formatCurrency(offer.invoiceDiscount.value)} invoice credit`}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Volume pricing tier for this customer */}
      <Card className={styles.pricingCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Money24Regular style={{ color: tokens.colorBrandForeground1 }} />
          <Text className={styles.sectionTitle} style={{ marginBottom: 0 }}>Effective Volume Pricing</Text>
        </div>
        <div className={styles.pricingGrid}>
          {effectivePricingTiers.map((tier) => {
            const isActive = sub.activeUsers >= tier.min && (tier.max === null || sub.activeUsers <= tier.max);
            const baseRate = getBaseRate(tier.min, tier.max);
            const isDiscountedTier = baseRate !== null && baseRate !== tier.rate;
            return (
              <div key={`${tier.min}-${tier.max ?? 'plus'}-${tier.rate}`} className={isActive ? styles.pricingBandActive : styles.pricingBand}>
                <Text className={styles.pricingRate}>{tier.label} users</Text>
                <Text className={styles.pricingRange}>{formatRate(tier.rate)}/user</Text>
                {isDiscountedTier && (
                  <Text size={200} style={{ color: tokens.colorPaletteGreenForeground1, marginTop: '4px' }}>
                    Was {formatRate(baseRate ?? tier.rate)}
                  </Text>
                )}
              </div>
            );
          })}
        </div>

        {sub.activeUsersHistory && sub.activeUsersHistory.length > 0 && (
          <div className={styles.sparklineContainer}>
            <Text className={styles.sparklineLabel}>Active Users Trend (Last 30 Days)</Text>
            <svg className={styles.sparklineSvg} viewBox="0 0 300 60" style={{ border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: '4px' }}>
              {(() => {
                const data = sub.activeUsersHistory!;
                const width = 300;
                const height = 60;
                const padding = 4;
                const drawWidth = width - 2 * padding;
                const drawHeight = height - 2 * padding;

                const minVal = Math.min(...data);
                const maxVal = Math.max(...data);
                const range = maxVal - minVal || 1;

                const points = data.map((val, idx) => {
                  const x = padding + (idx / (data.length - 1)) * drawWidth;
                  const y = padding + drawHeight - ((val - minVal) / range) * drawHeight;
                  return `${x},${y}`;
                });

                // Calculate linear trendline using least squares regression
                const n = data.length;
                let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
                data.forEach((val, idx) => {
                  sumX += idx;
                  sumY += val;
                  sumXY += idx * val;
                  sumX2 += idx * idx;
                });
                const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
                const intercept = (sumY - slope * sumX) / n;

                // Create trendline points
                const trendlinePoints = [0, n - 1].map((idx) => {
                  const trendVal = intercept + slope * idx;
                  const x = padding + (idx / (n - 1)) * drawWidth;
                  const y = padding + drawHeight - ((trendVal - minVal) / range) * drawHeight;
                  return `${x},${y}`;
                });

                const pathData = `M ${points.join(' L ')}`;
                const trendlinePathData = `M ${trendlinePoints.join(' L ')}`;

                return (
                  <>
                    <path d={pathData} stroke={tokens.colorBrandForeground1} strokeWidth="2" fill="none" />
                    <path d={trendlinePathData} stroke={tokens.colorBrandForeground1} strokeWidth="1.5" strokeDasharray="4,2" opacity="0.5" fill="none" />
                    <path d={`M ${points[0]} L ${points.join(' L ')} L ${points[points.length - 1].split(',')[0]},${height} L ${padding},${height}`} stroke={tokens.colorBrandForeground1} strokeWidth="0" fill={tokens.colorBrandForeground1} opacity="0.1" />
                  </>
                );
              })()}
            </svg>
          </div>
        )}

        {pricing.bands.length > 0 && (
          <>
            <Text weight="semibold" size={300}>Your cost breakdown</Text>
            {pricing.bands.map((b) => (
              <div key={`${b.tier.min}-${b.tier.max ?? 'plus'}-${b.tier.rate}`} className={styles.breakdownRow}>
                <span>{b.usersInBand} users × {formatRate(b.tier.rate)}/user</span>
                <span>{formatCurrency(b.bandCost)}</span>
              </div>
            ))}
            {pricing.discounts.map((discount) => (
              <div key={`${discount.label}-${discount.type}-${discount.value}`} className={styles.breakdownRow}>
                <span>{discount.label}</span>
                <span className={styles.savingsText}>-{formatCurrency(discount.saving)}</span>
              </div>
            ))}
            <div className={styles.breakdownTotal}>
              <span>Total ({pricing.users} active users)</span>
              <span>{formatCurrency(pricing.finalCost)}/month</span>
            </div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
              {pricing.totalDiscount > 0
                ? `RRP ${formatCurrency(basePricing.totalCost)} less ${formatCurrency(pricing.totalDiscount)} in active offers. Effective rate: ${formatRate(finalEffectiveRate)}/user`
                : `Effective rate: ${formatRate(pricing.effectiveRate)}/user`}
            </Text>
          </>
        )}
        <Text className={styles.activeNote}>
          Charges are calculated from active users and any current negotiated pricing or promotional offers.
        </Text>
      </Card>

      {/* Usage */}
      <Card className={styles.usageCard}>
        <Text className={styles.sectionTitle}>Usage</Text>
        <div className={styles.usageRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PeopleTeam24Regular style={{ width: 18, height: 18 }} />
            <Text className={styles.usageLabel}>Active users</Text>
          </div>
          <div className={styles.usageValues}>
            <Text className={styles.usageText}>
              {sub.activeUsers}{sub.licensedUsers > 0 ? ` / ${sub.licensedUsers} licensed` : ''}
            </Text>
          </div>
        </div>
        <div className={styles.usageRow} style={{ borderBottom: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Chat24Regular style={{ width: 18, height: 18 }} />
            <Text className={styles.usageLabel}>Conversations</Text>
          </div>
          <div className={styles.usageValues}>
            <Text className={styles.usageText}>{sub.conversationsUsed}</Text>
          </div>
        </div>
      </Card>

      {/* Estimated Next Invoice */}
      <Card className={styles.estimateCard}>
        <div className={styles.estimateHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Receipt24Regular style={{ color: tokens.colorBrandForeground1 }} />
            <Text className={styles.sectionTitle} style={{ marginBottom: 0 }}>Estimated Next Invoice</Text>
          </div>
          <Badge appearance="filled" color="informative">
            {sub.currentPeriod ? `Period ${sub.currentPeriod.periodNumber}` : 'Upcoming'}
          </Badge>
        </div>
        <Text className={styles.estimateAmount}>
          {formatCurrency(pricing.finalCost)}
        </Text>
        <Text className={styles.estimateLabel}>
          {pricing.users === 0
            ? 'No active users yet — no charges this period'
            : `Based on ${pricing.users} active user${pricing.users !== 1 ? 's' : ''} this period at ${formatRate(finalEffectiveRate)}/user after active offers`}
        </Text>
        {pricing.totalDiscount > 0 && (
          <Text size={200} style={{ color: tokens.colorPaletteGreenForeground1, marginTop: '8px', display: 'block' }}>
            You are currently saving {formatCurrency(pricing.totalDiscount)} versus list pricing this period.
          </Text>
        )}
        {sub.currentPeriod && (
          <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '8px' }}>
            Billing period: {new Date(sub.currentPeriod.start).toLocaleDateString()} – {new Date(sub.currentPeriod.end).toLocaleDateString()}
          </Text>
        )}
      </Card>

      {/* Invoices */}
      <Card className={styles.usageCard}>
        <Text className={styles.sectionTitle}>Invoice History</Text>
        {sub.invoices && sub.invoices.length > 0 ? (
          <table className={styles.invoicesTable}>
            <thead>
              <tr className={styles.invoiceHeaderRow}>
                <td className={styles.invoiceCell}>Period</td>
                <td className={styles.invoiceCell}>Users</td>
                <td className={styles.invoiceCell}>Amount</td>
                <td className={styles.invoiceCell}>Status</td>
                <td className={styles.invoiceCell}>Dates</td>
              </tr>
            </thead>
            <tbody>
              {sub.invoices.map((invoice) => (
                <tr key={invoice.id} className={styles.invoiceRow}>
                  <td className={styles.invoiceCell}>#{invoice.periodNumber}</td>
                  <td className={styles.invoiceCell}>{invoice.activeUsers}</td>
                  <td className={styles.invoiceCell}>
                    <span className={styles.invoiceAmount}>{formatCurrency(invoice.amount)}</span>
                  </td>
                  <td className={styles.invoiceCell}>
                    <Badge appearance="filled" color={invoice.status === 'paid' ? 'success' : invoice.status === 'pending' ? 'informative' : 'warning'}>
                      {invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : 'Unknown'}
                    </Badge>
                  </td>
                  <td className={styles.invoiceCell}>
                    {new Date(invoice.periodStart).toLocaleDateString()} – {new Date(invoice.periodEnd).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Text style={{ color: tokens.colorNeutralForeground3, fontSize: '13px' }}>No invoices yet — invoices are generated at the end of each billing period</Text>
        )}
      </Card>
    </div>
  );
}
